<template>
    <div>
        <Modal size="lg" @close="$emit('close')">
            <template #header>
                <div class="text-gray-600">
                    {{
                        isSuccess
                            ? $t("vue.frequency_updated_successfully")
                            : $t("vue.switch_billing_frequency")
                    }}
                </div>
            </template>

            <template #body>
                <div class="bg-white p-6">
                    <!-- Success State -->
                    <div v-if="isSuccess" class="space-y-6">
                        <div
                            class="rounded-md border border-green-200 bg-green-50 p-6 text-center"
                        >
                            <div
                                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100"
                            >
                                <svg
                                    class="h-6 w-6 text-green-600"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                            <h3 class="mt-4 text-lg font-medium text-green-800">
                                {{
                                    $t(
                                        "vue.frequency_updated_successfully_exclamation",
                                    )
                                }}
                            </h3>
                            <p class="mt-2 text-sm text-green-700">
                                {{ $t("vue.billing_frequency_changed_to") }}
                                <span class="font-medium">{{
                                    currentFrequency === "year"
                                        ? $t("vue.yearly")
                                        : $t("vue.monthly")
                                }}</span
                                >.
                            </p>
                        </div>

                        <div
                            class="rounded-md border border-blue-200 bg-blue-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.what_happens_next") }}
                            </h4>
                            <ul class="space-y-2 text-sm text-gray-700">
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{
                                        $t("vue.current_billing_cycle_continue")
                                    }}
                                    {{ formatNextBillingDate }}
                                </li>
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{ $t("vue.new_frequency_take_effect") }}
                                </li>
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{
                                        $t("vue.switch_back_previous_frequency")
                                    }}
                                </li>
                                <li class="flex items-start">
                                    <span class="mr-2 text-blue-600">•</span>
                                    {{ $t("vue.no_immediate_charges") }}
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Confirmation State -->
                    <div v-else class="space-y-6">
                        <!-- Current Plan Info -->
                        <div
                            class="rounded-md border border-gray-200 bg-gray-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.current_plan") }}
                            </h4>
                            <div class="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.plan") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        currentPlanName
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{
                                            $t("vue.current_frequency")
                                        }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        currentFrequency === "year"
                                            ? $t("vue.yearly")
                                            : $t("vue.monthly")
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.current_price") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800"
                                        >${{ currentPrice }}</span
                                    >
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.next_billing") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        formatNextBillingDate
                                    }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- New Plan Info -->
                        <div
                            class="rounded-md border border-blue-200 bg-blue-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.new_billing_frequency") }}
                            </h4>
                            <div
                                v-if="isLoadingPreview"
                                class="flex items-center justify-center py-4"
                            >
                                <div class="text-gray-600">
                                    Loading preview...
                                </div>
                            </div>
                            <div
                                v-else-if="previewData"
                                class="grid grid-cols-2 gap-4 text-sm"
                            >
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.plan") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        currentPlanName
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.new_frequency") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        newFrequency === "year"
                                            ? $t("vue.yearly")
                                            : $t("vue.monthly")
                                    }}</span>
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.new_price") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800"
                                        >${{ newTotal }}</span
                                    >
                                </div>
                                <div>
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.effective_date") }}:</span
                                    >
                                    <span class="ml-2 text-gray-800">{{
                                        formatEffectiveDate
                                    }}</span>
                                </div>
                                <div class="col-span-2">
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.immediate_charge") }}:</span
                                    >
                                    <span
                                        class="ml-2"
                                        :class="immediateTransactionClass"
                                    >
                                        {{ immediateTransactionText }}
                                    </span>
                                    <span
                                        v-if="isChargeCoveredByCredits"
                                        class="ml-2 font-medium text-green-600"
                                    >
                                        ✓
                                        {{ $t("vue.fully_covered_by_credits") }}
                                    </span>
                                    <span
                                        v-else-if="
                                            isChargePartiallyCoveredByCredits
                                        "
                                        class="ml-2 font-medium text-green-600"
                                    >
                                        ✓
                                        {{
                                            $t(
                                                "vue.partially_covered_by_credits",
                                            )
                                        }}
                                    </span>
                                </div>
                                <div v-if="creditsUsed" class="col-span-2">
                                    <span class="font-medium text-gray-600"
                                        >{{ $t("vue.credits_used") }}:</span
                                    >
                                    <span class="ml-2 text-green-600">
                                        ${{ creditsUsed }}
                                    </span>
                                </div>
                            </div>
                            <div v-else class="text-sm text-red-600">
                                Failed to load preview data
                            </div>
                        </div>

                        <!-- Important Notes -->
                        <div
                            class="rounded-md border border-yellow-200 bg-yellow-50 p-4"
                        >
                            <h4 class="mb-2 text-lg font-medium text-gray-800">
                                {{ $t("vue.important_information") }}
                            </h4>
                            <ul class="space-y-1 text-sm text-gray-700">
                                <li
                                    v-if="
                                        immediateTransactionAmount &&
                                        immediateTransactionAmount !== '0'
                                    "
                                >
                                    <span v-if="isChargeCoveredByCredits">
                                        •
                                        {{ $t("vue.immediate_charge_applied") }}
                                        {{ immediateTransactionText }},
                                        {{
                                            $t(
                                                "vue.but_fully_covered_by_existing_credits",
                                            )
                                        }}
                                    </span>
                                    <span
                                        v-else-if="
                                            isChargePartiallyCoveredByCredits
                                        "
                                    >
                                        •
                                        {{ $t("vue.immediate_charge_applied") }}
                                        {{ immediateTransactionText }},
                                        {{
                                            $t(
                                                "vue.partially_covered_by_existing_credits",
                                            )
                                        }}
                                    </span>
                                    <span v-else>
                                        •
                                        {{ $t("vue.immediate_charge_applied") }}
                                        {{ immediateTransactionText }}
                                        {{
                                            $t(
                                                "vue.will_be_applied_to_your_account",
                                            )
                                        }}
                                    </span>
                                </li>
                                <li v-else>
                                    •
                                    {{
                                        $t(
                                            "vue.no_immediate_charges_will_be_made_for_this_change",
                                        )
                                    }}
                                </li>
                                <li>
                                    •
                                    {{
                                        $t(
                                            "vue.your_current_billing_cycle_will_continue_until_the_next_billing_date",
                                        )
                                    }}
                                </li>
                                <li>
                                    •
                                    {{
                                        $t(
                                            "vue.the_new_frequency_will_take_effect",
                                        )
                                    }}
                                    {{
                                        immediateTransactionAmount &&
                                        immediateTransactionAmount !== "0"
                                            ? $t("vue.immediately")
                                            : $t(
                                                  "vue.on_your_next_billing_cycle",
                                              )
                                    }}
                                </li>
                                <li>
                                    •
                                    {{
                                        $t(
                                            "vue.you_can_switch_back_to_your_previous_frequency_at_any_time",
                                        )
                                    }}
                                </li>
                                <li
                                    v-if="
                                        creditsUsed &&
                                        !isChargeCoveredByCredits &&
                                        !isChargePartiallyCoveredByCredits
                                    "
                                >
                                    • {{ $t("vue.credits_of") }} ${{
                                        creditsUsed
                                    }}
                                    {{
                                        $t(
                                            "vue.will_be_applied_to_your_account",
                                        )
                                    }}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </template>

            <template #footer>
                <div
                    class="flex justify-end space-x-3 border-t border-gray-200 px-6 py-4"
                >
                    <AtomicButton
                        v-if="!isSuccess"
                        color="white"
                        @click="$emit('close')"
                    >
                        {{ $t("vue.cancel") }}
                    </AtomicButton>
                    <AtomicButton
                        v-if="!isSuccess"
                        color="yellow"
                        :disabled="isLoading"
                        @click="confirmFrequencySwitch"
                    >
                        <span v-if="isLoading">{{ $t("vue.processing") }}</span>
                        <span v-else>{{ $t("vue.confirm_switch") }}</span>
                    </AtomicButton>
                    <AtomicButton
                        v-if="isSuccess"
                        color="yellow"
                        @click="$emit('close')"
                    >
                        {{ $t("vue.close") }}
                    </AtomicButton>
                </div>
            </template>
        </Modal>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { format, add } from "date-fns";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";

const props = defineProps({
    currentFrequency: {
        type: String as () => "month" | "year",
        required: true,
    },
    currentPrice: {
        type: String,
        required: true,
    },
    currentPlanName: {
        type: String,
        required: true,
    },
    nextBillingDate: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(["close", "success"]);
const isLoading = ref(false);
const isSuccess = ref(false);
const previewData = ref(null as any);
const isLoadingPreview = ref(false);

const newFrequency = computed(() => {
    return props.currentFrequency === "year" ? "month" : "year";
});

const formatNextBillingDate = computed(() => {
    if (!props.nextBillingDate) return "N/A";
    return format(new Date(props.nextBillingDate), "MMMM d, yyyy");
});

const formatEffectiveDate = computed(() => {
    if (
        immediateTransactionAmount.value &&
        immediateTransactionAmount.value !== "0"
    ) {
        return "Immediate";
    }
    if (!props.nextBillingDate) return "N/A";
    return format(new Date(props.nextBillingDate), "MMMM d, yyyy");
});

const priceChangeClass = computed(() => {
    if (!previewData.value) return "text-gray-600";

    const priceChange = previewData.value.price_change;

    if (priceChange > 0) {
        return "text-red-600 font-medium";
    } else if (priceChange < 0) {
        return "text-green-600 font-medium";
    }
    return "text-gray-600";
});

const priceChangeText = computed(() => {
    if (!previewData.value) return "No change";

    const priceChange = previewData.value.price_change;

    if (priceChange > 0) {
        return `+$${priceChange.toFixed(2)}`;
    } else if (priceChange < 0) {
        return `-$${Math.abs(priceChange).toFixed(2)}`;
    }
    return "No change";
});

const newPrice = computed(() => {
    return previewData.value?.new_price?.toString() || "0";
});

const newTotal = computed(() => {
    return previewData.value?.new_total?.toString() || "0";
});

const immediateTransactionAmount = computed(() => {
    return previewData.value?.immediate_transaction_total?.toString() || "0";
});

const immediateTransactionClass = computed(() => {
    if (!immediateTransactionAmount.value) return "text-gray-600";

    if (immediateTransactionAmount.value > 0) {
        return "text-red-600 font-medium";
    } else if (immediateTransactionAmount.value < 0) {
        return "text-green-600 font-medium";
    }
    return "text-gray-600";
});

const immediateTransactionText = computed(() => {
    const amount = parseFloat(immediateTransactionAmount.value);
    if (amount < 0) {
        return `-$${Math.abs(amount).toFixed(2)} (credit)`;
    }
    return `$${amount.toFixed(2)}`;
});

const creditsUsed = computed(() => {
    if (!previewData.value?.credits_used) return null;
    return Math.abs(parseFloat(previewData.value.credits_used)).toFixed(2);
});

const isChargeCoveredByCredits = computed(() => {
    if (!immediateTransactionAmount.value || !creditsUsed.value) return false;
    const chargeAmount = parseFloat(immediateTransactionAmount.value);
    const creditAmount = parseFloat(creditsUsed.value);
    return chargeAmount > 0 && creditAmount >= chargeAmount;
});

const isChargePartiallyCoveredByCredits = computed(() => {
    if (!immediateTransactionAmount.value || !creditsUsed.value) return false;
    const chargeAmount = parseFloat(immediateTransactionAmount.value);
    const creditAmount = parseFloat(creditsUsed.value);
    return chargeAmount > 0 && creditAmount > 0 && creditAmount < chargeAmount;
});

const fetchPreviewData = async () => {
    isLoadingPreview.value = true;

    try {
        const response = await axios.get(
            route("billing.subscriptions.frequency.preview"),
        );
        previewData.value = response.data;
    } catch (error) {
        console.error("Failed to fetch preview data:", error);
    } finally {
        isLoadingPreview.value = false;
    }
};

const confirmFrequencySwitch = async () => {
    isLoading.value = true;

    try {
        await router.post(
            route("billing.subscriptions.frequency.update"),
            {},
            {
                onSuccess: () => {
                    isSuccess.value = true;
                    isLoading.value = false;
                    // Emit success event to parent
                    emit("success");
                },
                onError: (errors) => {
                    console.error("Frequency switch failed:", errors);
                    // You might want to show an error message here
                    isLoading.value = false;
                },
                onFinish: () => {
                    isLoading.value = false;
                },
            },
        );
    } catch (error) {
        console.error("Frequency switch error:", error);
        isLoading.value = false;
    }
};

// Fetch preview data when modal opens
onMounted(() => {
    fetchPreviewData();
});
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
